import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { RefreshFailureReason } from '@generated/api';
import styles from './SessionEndedBanner.module.css';

export interface ISessionEndedBannerProps {
  reason: RefreshFailureReason;
}

const COPY: Record<RefreshFailureReason, string> = {
  [RefreshFailureReason.IdleTimeout]: "You've been signed out due to inactivity.",
  [RefreshFailureReason.Expired]: 'Your session has ended. Please sign in to continue.',
  [RefreshFailureReason.ReplayDetected]:
    'For your security, this session has been ended. Please sign in again.',
  [RefreshFailureReason.SessionSuperseded]:
    "You were signed out because your account signed in on another device. If that wasn't you, reset your password.",
  [RefreshFailureReason.Unknown]: 'Your session has ended. Please sign in.',
  // NOTE (84-tbill-order-settlement Wave 5 regen): `Revoked` was dropped from
  // RefreshFailureReason when the client-api swagger was regenerated on feat/84
  // (cookie-based refresh/logout auth change). The map entry is removed to keep
  // the exhaustive Record<RefreshFailureReason,…> type-checking. See DEVIATIONS —
  // the auth owner should confirm this enum removal is intended.
};

const SessionEndedBanner: React.FC<ISessionEndedBannerProps> = ({ reason }) => {
  const isWarning = reason === RefreshFailureReason.ReplayDetected;
  const Icon = isWarning ? AlertTriangle : Info;

  return (
    <div
      role={isWarning ? 'alert' : 'status'}
      aria-live={isWarning ? 'assertive' : 'polite'}
      className={`${styles.banner} ${isWarning ? styles.warning : ''}`}
      data-testid="session-ended-banner"
      data-reason={reason}
    >
      <Icon size={18} className={styles.icon} aria-hidden="true" />
      <span className={styles.message}>{COPY[reason]}</span>
    </div>
  );
};

export default SessionEndedBanner;
